import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

/**
 * CareersConfigEditor — form-based editor for the `careers_config` KV.
 * All fields land in a single KV document, consumed by /carreiras via
 * `useLangKV("careers_config")` — no backend changes needed.
 */
const DEFAULTS = {
  // Hero
  hero_eyebrow: "Trabalhe conosco",
  hero_title: "Venha ser Placer:",
  hero_highlight: "faça parte do nosso time.",
  hero_subtitle: "A gente constrói o futuro do marketing imobiliário no Brasil. E quer os melhores talentos por perto pra isso.",
  // Sobre nós
  about_eyebrow: "Sobre nós",
  about_title: "Um time. Um propósito.",
  about_paragraphs: [
    "Somos uma agência de marketing imobiliário com quase 4 décadas de estrada. Trabalhamos com os maiores nomes do país e transformamos empreendimentos em cases inesquecíveis.",
    "Aqui, as pessoas vêm antes do resultado — e é por isso que os resultados vêm.",
  ],
  // Cultura
  culture_eyebrow: "Cultura",
  culture_title: "Independente da vaga, você é a pessoa ideal se…",
  culture_bullets: [],
  // Vagas
  jobs_eyebrow: "Oportunidades",
  jobs_title: "Vagas Abertas",
  jobs_empty_prefix: "Não há vagas abertas no momento. Envie seu currículo mesmo assim pelo e-mail",
  jobs_email: "pessoas@agenciaplaces.com.br",
  job_expand_label: "Saiba mais",
  job_overview_label: "Visão geral do cargo",
  job_responsibilities_label: "Responsabilidades",
  job_requirements_label: "Requisitos",
  job_extras_label: "Informações adicionais",
  job_apply_label: "APLICAR",
  // Vídeos
  videos_eyebrow: "Bastidores",
  videos_title: "Como é trabalhar aqui.",
};

const SECTIONS = [
  {
    id: "hero", title: "Hero (topo da página)",
    fields: [
      { k: "hero_eyebrow", label: "Eyebrow (rótulo pequeno)", hint: 'Ex: "Trabalhe conosco"' },
      { k: "hero_title", label: "Título principal (parte preta)", hint: 'Ex: "Venha ser Placer:"' },
      { k: "hero_highlight", label: "Destaque (parte laranja)", hint: 'Ex: "faça parte do nosso time."' },
      { k: "hero_subtitle", label: "Subtítulo", textarea: true },
    ],
  },
  {
    id: "about", title: "Sobre nós",
    fields: [
      { k: "about_eyebrow", label: "Eyebrow" },
      { k: "about_title", label: "Título da seção", hint: 'Ex: "Um time. Um propósito."' },
    ],
    list: { key: "about_paragraphs", label: "Parágrafos", placeholder: "Escreva um parágrafo…", cta: "Adicionar parágrafo" },
  },
  {
    id: "culture", title: "Cultura (você é a pessoa ideal se…)",
    fields: [
      { k: "culture_eyebrow", label: "Eyebrow" },
      { k: "culture_title", label: "Título da seção" },
    ],
    list: { key: "culture_bullets", label: "Bullets (cards numerados)", placeholder: "Escreva um tópico…", cta: "Adicionar tópico", numbered: true },
  },
  {
    id: "jobs", title: "Vagas Abertas",
    fields: [
      { k: "jobs_eyebrow", label: "Eyebrow" },
      { k: "jobs_title", label: "Título" },
      { k: "jobs_empty_prefix", label: "Texto quando não há vagas abertas", textarea: true, hint: "O e-mail aparece automaticamente ao final desta frase." },
      { k: "jobs_email", label: "E-mail para currículo", hint: "Aparece como link clicável no fim da mensagem acima." },
      { k: "job_expand_label", label: 'Rótulo "Saiba mais"' },
      { k: "job_overview_label", label: 'Rótulo "Visão geral do cargo"' },
      { k: "job_responsibilities_label", label: 'Rótulo "Responsabilidades"' },
      { k: "job_requirements_label", label: 'Rótulo "Requisitos"' },
      { k: "job_extras_label", label: 'Rótulo "Informações adicionais"' },
      { k: "job_apply_label", label: 'Rótulo do botão "APLICAR"' },
    ],
  },
  {
    id: "videos", title: "Como é trabalhar aqui (vídeos)",
    fields: [
      { k: "videos_eyebrow", label: "Eyebrow" },
      { k: "videos_title", label: "Título" },
    ],
  },
];

export default function CareersConfigEditor() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/content/kv/careers_config")
      .then(({ data }) => data && typeof data === "object" && setCfg({ ...DEFAULTS, ...data }))
      .finally(() => setLoading(false));
  }, []);

  const upd = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const updList = (key, i, v) => setCfg((s) => { const a = [...(s[key] || [])]; a[i] = v; return { ...s, [key]: a }; });
  const addItem = (key) => setCfg((s) => ({ ...s, [key]: [...(s[key] || []), ""] }));
  const removeItem = (key, i) => setCfg((s) => ({ ...s, [key]: (s[key] || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...cfg,
        about_paragraphs: (cfg.about_paragraphs || []).map((s) => (s || "").trim()).filter(Boolean),
        culture_bullets: (cfg.culture_bullets || []).map((s) => (s || "").trim()).filter(Boolean),
      };
      await api.put("/content/kv/careers_config", { value: clean });
      setCfg(clean);
      toast.success("Config Carreiras salva.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-end mb-6 sticky top-0 bg-white z-10 pt-2 pb-3">
        <div>
          <h1 className="font-display text-4xl">Config Carreiras</h1>
          <div className="text-xs text-neutral-500 mt-1">Todos os textos da página <b>/carreiras</b>. Salvar aplica imediatamente.</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="careers-cfg-save" className="text-xs px-4 py-2 bg-black text-white rounded-full disabled:opacity-40 hover:bg-accent-orange transition-colors">
          <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>

      <div className="space-y-5">
        {SECTIONS.map((sec) => (
          <section key={sec.id} className="border border-black/10 rounded-md p-5" data-testid={`careers-section-${sec.id}`}>
            <div className="font-semibold text-sm mb-4">{sec.title}</div>
            <div className="space-y-3">
              {sec.fields.map((f) => (
                <Field key={f.k} label={f.label} hint={f.hint}>
                  {f.textarea ? (
                    <textarea rows={3} value={cfg[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`careers-${f.k}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  ) : (
                    <input value={cfg[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`careers-${f.k}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  )}
                </Field>
              ))}
            </div>

            {sec.list && (
              <div className="mt-4">
                <div className="text-xs text-neutral-500 mb-2">{sec.list.label}</div>
                <div className="space-y-2">
                  {(cfg[sec.list.key] || []).map((v, i) => (
                    <div key={i} className="flex items-start gap-2">
                      {sec.list.numbered && (
                        <div className="w-6 h-6 mt-1 rounded-full bg-accent-orange/20 text-accent-orange flex items-center justify-center font-bold text-[10px]">{String(i + 1).padStart(2, "0")}</div>
                      )}
                      <textarea rows={2} value={v} placeholder={sec.list.placeholder} onChange={(e) => updList(sec.list.key, i, e.target.value)} data-testid={`careers-${sec.list.key}-${i}`} className="flex-1 border border-black/15 px-3 py-2 rounded text-sm" />
                      <button onClick={() => removeItem(sec.list.key, i)} className="text-red-500 hover:bg-red-50 p-2 rounded" data-testid={`careers-${sec.list.key}-del-${i}`} title="Remover"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => addItem(sec.list.key)} className="mt-3 text-xs bg-accent-yellow px-3 py-1.5 rounded-full font-semibold hover:bg-black hover:text-white transition-colors" data-testid={`careers-${sec.list.key}-add`}>
                  <Plus className="w-3 h-3 inline" /> {sec.list.cta}
                </button>
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700 block mb-1">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}
